import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gbp = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);

const MAX_ATTEMPTS = 10;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("payment_intent") || params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          try {
            const res = await axios.get(`${API}/payments/order/${sessionId}`);
            setOrder(res.data);
          } catch (e) {}
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="bg-[#f2e5de] min-h-screen flex flex-col" data-testid="payment-success-page">
      <Navbar />
      <section className="feral-texture flex-1 flex items-center justify-center px-6 py-32">
        <div className="max-w-lg w-full bg-white border border-[#d9c3a8] rounded-lg shadow-xl p-8 md:p-10 text-center">
          {state === "polling" && (
            <div data-testid="payment-polling">
              <Loader2 size={48} className="text-[#9b3d0a] animate-spin mx-auto mb-6" />
              <h1 className="feral-serif text-[#9b3d0a] text-3xl mb-3">
                Confirming your payment…
              </h1>
              <p className="text-[#6e5646]">Just a moment while we confirm with your bank.</p>
            </div>
          )}

          {state === "paid" && (
            <div data-testid="payment-confirmed">
              <CheckCircle2 size={56} className="text-[#5b8049] mx-auto mb-6" />
              <h1 className="feral-serif text-[#9b3d0a] text-4xl mb-3">You're booked in!</h1>
              <p className="text-[#6e5646] mb-6">
                Thank you{order ? ` for booking ${order.event_name}` : ""}. A confirmation
                email is on its way.
              </p>
              {order && (
                <div className="text-left bg-[#f3e7d6] border border-[#d9c3a8] rounded-md p-5 mb-6">
                  <ul className="space-y-2">
                    {order.lines.map((l, i) => (
                      <li key={i} className="flex justify-between text-[#1e231f] text-sm">
                        <span>
                          {l.qty}× {l.name}
                        </span>
                        <span className="font-semibold">{gbp(l.price * l.qty)}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="border-t border-[#d9c3a8] mt-4 pt-3 flex justify-between">
                    <span className="text-[#1e231f] font-semibold">
                      {order.installment_plan ? "Paid today" : "Total paid"}
                    </span>
                    <span className="feral-serif text-[#9b3d0a] text-xl">
                      {gbp(
                        order.installment_plan
                          ? (order.installment_plan.first_amount ?? order.installment_plan.installment_amount)
                          : order.amount
                      )}
                    </span>
                  </div>
                  {order.installment_plan && (
                    <div className="mt-3 bg-white/60 border border-[#e0c9ad] rounded-sm px-3 py-3 text-sm text-[#5b4a37]" data-testid="success-plan-schedule">
                      <p className="font-bold text-[#7a6a58] text-xs tracking-[0.1em] uppercase mb-1.5">Your payment plan</p>
                      <p className="leading-snug">
                        {gbp(order.installment_plan.first_amount ?? order.installment_plan.installment_amount)} paid today.
                        The remaining <strong>{Math.max((order.installment_plan.count || 4) - 1, 0)}</strong> payments of{" "}
                        <strong>{gbp(order.installment_plan.installment_amount)}</strong> will be taken automatically from your card, one month apart.
                      </p>
                      <p className="mt-2 text-xs text-[#9a876f]">
                        Booking total: {gbp(order.amount)} · {order.installment_plan.count || 4} monthly payments
                      </p>
                    </div>
                  )}
                </div>
              )}
              <Link
                to="/events"
                data-testid="back-to-events-button"
                className="inline-block bg-[#9b3d0a] hover:bg-[#7e3208] text-[#f2e5de] px-8 py-3 tracking-[0.12em] text-sm font-semibold transition-colors"
              >
                BACK TO EVENTS
              </Link>
            </div>
          )}

          {(state === "timeout" || state === "error") && (
            <div data-testid="payment-issue">
              <AlertCircle size={48} className="text-[#ebc754] mx-auto mb-6" />
              <h1 className="feral-serif text-[#9b3d0a] text-3xl mb-3">
                {state === "timeout" ? "Still processing…" : "Something went wrong"}
              </h1>
              <p className="text-[#6e5646] mb-6">
                {state === "timeout"
                  ? "Your payment is taking a little longer than usual. If you were charged, you'll receive a confirmation email shortly."
                  : "We couldn't confirm this payment. If you were charged, please contact us and we'll sort it out."}
              </p>
              <Link
                to="/events"
                className="inline-block bg-[#9b3d0a] hover:bg-[#7e3208] text-[#f2e5de] px-8 py-3 tracking-[0.12em] text-sm font-semibold transition-colors"
              >
                BACK TO EVENTS
              </Link>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
